import React, { Suspense } from "react";
import { lazy } from './utils/lazyWithRetry';
import { BrowserRouter, Routes as RouterRoutes, Route, Navigate, useLocation } from "react-router-dom";
import { useEffect } from 'react';
import { cleanupOverlays } from './utils/overlayCleanup';
import ScrollToTop from "components/ScrollToTop";
import ErrorBoundary from "components/ErrorBoundary";
import ProtectedRoute from './components/ProtectedRoute';
import StaffLoginRedirect from './components/StaffLoginRedirect';
import Layout from './components/Layout';
import { SupportTicketResolvedProvider } from './context/SupportTicketResolvedContext';
import { FEATURE_KEYS } from './context/SubscriptionContext';
import Icon from './components/AppIcon';

const Login = lazy(() => import('./pages/Login'));
const AuthHandoff = lazy(() => import('./pages/AuthHandoff'));
const ResetPassword = lazy(() => import('./pages/ResetPassword'));
const BillingComplete = lazy(() => import('./pages/billing/BillingComplete'));
const OrganizationBillingPage = lazy(() => import('./pages/billing/OrganizationBillingPage'));
const ProfilePage = lazy(() => import('./pages/profile/ProfilePage'));
const OrganisationDirectoryPage = lazy(() => import('./pages/organisation/OrganisationDirectoryPage'));
const OnboardingPage = lazy(() => import('./pages/onboarding/OnboardingPage'));

// ── Lazy-loaded pages (code-split per route) ──────────────
const NotFound = lazy(() => import("pages/NotFound"));
const Help = lazy(() => import('./pages/help'));
const CostAnalytics = lazy(() => import('./pages/cost-analytics'));
const MainDashboard = lazy(() => import('./pages/main-dashboard'));
const CostBreakdownPage = lazy(() => import('./pages/cost-analytics/CostBreakdownPage'));
const MealDetailView = lazy(() => import('./pages/meal-detail-view'));
const ComplianceReporting = lazy(() => import('./pages/compliance-reporting'));
const FoodCalendarView = lazy(() => import('./pages/food-calendar-view'));
const MealsManagement = lazy(() => import('./pages/meals-management'));
const IngredientsManagement = lazy(() => import('./pages/ingredients-management'));
const DeliveryStatusPage = lazy(() => import('./pages/delivery-status'));
const AdminDashboard = lazy(() => import('./pages/admin/admin-dashboard'));
const UserManagement = lazy(() => import('./pages/admin/user-management'));
const CareHomeManagement = lazy(() => import('./pages/admin/care-home-management'));
const ResidentManagement = lazy(() => import('./pages/admin/resident-management'));
const SuperAdminPortal = lazy(() => import('./pages/admin/super-admin/SuperAdminPortal'));
const MealApprovals = lazy(() => import('./pages/admin/super-admin/MealApprovals'));
const UserActivityTracker = lazy(() => import('./pages/admin/super-admin/UserActivityTracker'));
const EmailDigestPanel = lazy(() => import('./pages/admin/super-admin/EmailDigestPanel'));
const SupportTicketsPage = lazy(() => import('./pages/admin/super-admin/SupportTicketsPage'));
const TicketPage = lazy(() => import('./pages/support/TicketPage'));
const AuditLogsPage = lazy(() => import('./pages/audit-logs'));
const Community = lazy(() => import('./pages/community'));

// Minimal spinner for auth pages and lazy route chunks
const PageLoader = () => (
  <div className="min-h-screen flex items-center justify-center bg-background">
    <div className="w-10 h-10 border-[3px] border-primary/20 border-t-primary rounded-full animate-spin" />
  </div>
);

const AuthPage = ({ children }) => (
  <Suspense fallback={<PageLoader />}>{children}</Suspense>
);

// Wrapper component to provide Layout for protected pages
const LayoutWrapper = ({ children }) => (
  <SupportTicketResolvedProvider>
    <Layout>{children}</Layout>
  </SupportTicketResolvedProvider>
);

const Routes = () => {
  // Global cleanup component: runs on route change to remove any stuck overlays/backdrops
  const OverlayCleanup = () => {
    const location = useLocation();
    useEffect(() => {
      // Run cleanup after a microtask to avoid blocking the route render
      const id = requestAnimationFrame(() => cleanupOverlays());

      // Also install Esc handler to allow manual cleanup
      const onKey = (e) => {
        if (e.key === 'Escape') cleanupOverlays();
      };
      window.addEventListener('keydown', onKey);
      return () => {
        cancelAnimationFrame(id);
        window.removeEventListener('keydown', onKey);
      };
    }, [location.pathname]);
    return null;
  };

  return (
    <BrowserRouter>
      <ErrorBoundary>
      <ScrollToTop />
      <OverlayCleanup />
      <Suspense fallback={<PageLoader />}>
      <RouterRoutes>
        {/* Authentication Routes - No Layout */}
        <Route path="/auth/handoff" element={<AuthPage><AuthHandoff /></AuthPage>} />
        <Route path="/login" element={<AuthPage><Login /></AuthPage>} />
        <Route path="/reset-password" element={<AuthPage><ResetPassword /></AuthPage>} />
        <Route path="/onboarding" element={<AuthPage><OnboardingPage /></AuthPage>} />
        <Route path="/billing/complete" element={<ProtectedRoute><AuthPage><BillingComplete /></AuthPage></ProtectedRoute>} />
        
        {/* Application Routes - Protected with Layout + feature gating */}
        <Route path="/" element={<Navigate to="/main-dashboard" replace />} />
        <Route path="/main-dashboard" element={<ProtectedRoute requiredFeature={FEATURE_KEYS.DASHBOARD}><LayoutWrapper><MainDashboard /></LayoutWrapper></ProtectedRoute>} />
        <Route path="/billing" element={<ProtectedRoute><LayoutWrapper><OrganizationBillingPage /></LayoutWrapper></ProtectedRoute>} />
        <Route path="/profile" element={<ProtectedRoute><LayoutWrapper><ProfilePage /></LayoutWrapper></ProtectedRoute>} />
        <Route path="/organisation" element={<ProtectedRoute><LayoutWrapper><OrganisationDirectoryPage /></LayoutWrapper></ProtectedRoute>} />
        <Route path="/food-calendar-view" element={<ProtectedRoute requiredFeature={FEATURE_KEYS.CALENDAR}><LayoutWrapper><FoodCalendarView /></LayoutWrapper></ProtectedRoute>} />
        <Route path="/delivery-status" element={<ProtectedRoute requiredFeature={FEATURE_KEYS.DELIVERY_STATUS}><LayoutWrapper><DeliveryStatusPage /></LayoutWrapper></ProtectedRoute>} />
        <Route path="/meals-management" element={<ProtectedRoute requiredFeature={FEATURE_KEYS.MEALS}><LayoutWrapper><MealsManagement /></LayoutWrapper></ProtectedRoute>} />
        <Route path="/help" element={<ProtectedRoute requiredFeature={FEATURE_KEYS.HELP}><LayoutWrapper><Help /></LayoutWrapper></ProtectedRoute>} />
        <Route path="/community" element={<ProtectedRoute requiredFeature={FEATURE_KEYS.COMMUNITY}><LayoutWrapper><Community /></LayoutWrapper></ProtectedRoute>} />
        <Route path="/meal-detail-view" element={<ProtectedRoute requiredFeature={FEATURE_KEYS.MEALS}><LayoutWrapper><MealDetailView /></LayoutWrapper></ProtectedRoute>} />
        
        {/* Paid feature routes - gated by subscription */}
        <Route path="/ingredients-management" element={<ProtectedRoute requiredFeature={FEATURE_KEYS.INGREDIENTS}><LayoutWrapper><IngredientsManagement /></LayoutWrapper></ProtectedRoute>} />
        <Route path="/cost-analytics" element={<ProtectedRoute requiredFeature={FEATURE_KEYS.ANALYTICS}><LayoutWrapper><CostAnalytics /></LayoutWrapper></ProtectedRoute>} />
        <Route path="/cost-analytics/breakdown" element={<ProtectedRoute requiredFeature={FEATURE_KEYS.ANALYTICS}><LayoutWrapper><CostBreakdownPage /></LayoutWrapper></ProtectedRoute>} />
        <Route path="/audit-logs" element={<ProtectedRoute requireSuperAdmin><LayoutWrapper><AuditLogsPage /></LayoutWrapper></ProtectedRoute>} />
        <Route path="/compliance-reporting" element={<ProtectedRoute requiredFeature={FEATURE_KEYS.COMPLIANCE}><LayoutWrapper><ComplianceReporting /></LayoutWrapper></ProtectedRoute>} />
        
        {/* Admin Routes - Protected with Layout + feature gating */}
        <Route path="/admin" element={<Navigate to="/admin/dashboard" replace />} />
        <Route path="/admin/dashboard" element={<ProtectedRoute requireAdmin><LayoutWrapper><AdminDashboard /></LayoutWrapper></ProtectedRoute>} />
        <Route path="/admin/user-management" element={<ProtectedRoute requiredFeature={FEATURE_KEYS.ADMIN_USERS}><LayoutWrapper><UserManagement /></LayoutWrapper></ProtectedRoute>} />
        <Route path="/admin/care-home-management" element={<ProtectedRoute requiredFeature={FEATURE_KEYS.ADMIN_CARE_HOMES}><LayoutWrapper><CareHomeManagement /></LayoutWrapper></ProtectedRoute>} />
        <Route path="/admin/resident-management" element={<ProtectedRoute requiredFeature={FEATURE_KEYS.ADMIN_RESIDENTS}><LayoutWrapper><ResidentManagement /></LayoutWrapper></ProtectedRoute>} />
        
        {/* Super Admin Routes - unified Admin Portal */}
        <Route path="/super-admin" element={<ProtectedRoute requireSuperAdmin><LayoutWrapper><SuperAdminPortal /></LayoutWrapper></ProtectedRoute>} />
        {/* Legacy paths now folded into the unified portal */}
        <Route path="/super-admin/subscriptions" element={<Navigate to="/super-admin" replace />} />
        <Route path="/super-admin/user-control" element={<Navigate to="/super-admin" replace />} />
        <Route path="/super-admin/meal-approvals" element={<ProtectedRoute requireSuperAdmin><LayoutWrapper><MealApprovals /></LayoutWrapper></ProtectedRoute>} />
        <Route path="/super-admin/user-activity" element={<ProtectedRoute requireSuperAdmin><LayoutWrapper><UserActivityTracker /></LayoutWrapper></ProtectedRoute>} />
        <Route path="/super-admin/email-digest" element={<ProtectedRoute requireSuperAdmin><LayoutWrapper><EmailDigestPanel /></LayoutWrapper></ProtectedRoute>} />
        <Route path="/super-admin/support-tickets" element={<ProtectedRoute requireSuperAdmin><LayoutWrapper><SupportTicketsPage /></LayoutWrapper></ProtectedRoute>} />
        <Route path="/support/ticket/:ticketId" element={<ProtectedRoute><LayoutWrapper><TicketPage /></LayoutWrapper></ProtectedRoute>} />

        {/* Staff Routes - Protected with Layout */}
        <Route path="/staff" element={<Navigate to="/staff/dashboard" replace />} />
        <Route path="/staff/dashboard" element={<ProtectedRoute requiredFeature={FEATURE_KEYS.DASHBOARD}><LayoutWrapper><MainDashboard /></LayoutWrapper></ProtectedRoute>} />
        <Route path="/staff/residents" element={<Navigate to="/staff/dashboard" replace />} />
        
        <Route path="*" element={<NotFound />} />
      </RouterRoutes>
      </Suspense>
      </ErrorBoundary>
    </BrowserRouter>
  );
};

export default Routes;
